import type { FC, ReactNode } from "react";
import styled, { css } from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { setFont, setFontVariant } from "@similarweb/styles";
import { $robotoFontFamily } from "@similarweb/styles";
import {
    StyledTableBody,
    StyledTableColumn,
    StyledTableHeader,
    StyledWrapperTableBody,
} from "app/pages/workspace/sales/components/custom-table/Table/styles";
import {
    StyledTableCell,
    StyledTableHeaderCell,
} from "app/pages/workspace/sales/components/custom-table/TableCell/styles";
import { SWReactIcons } from "@similarweb/icons";
import { Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import type { IEllipsisDropdownItemProps } from "@similarweb/ui-components";
import { StyledDropdownContent } from "app/pages/workspace/sales/components/custom-dropdown/DropdownContent/styles";

import { CRM_EXPORT_DROPDOWN_WIDTH } from "./cells/constants";

export const StyledTable = styled.div`
    border-top: 1px solid ${colorsPalettes.carbon["50"]};
    width: 100%;
    height: 100%;
    position: relative;
    overflow: auto;

    ${StyledTableHeader} {
        background-color: ${colorsPalettes.carbon["25"]};
        cursor: default;
        justify-content: flex-end;
        flex: none;

        ${StyledTableColumn} {
            border-right: 1px solid ${colorsPalettes.carbon["50"]};
            &:first-child,
            &:nth-child(2) {
                border-right: none;
            }

            &:nth-child(-n + 2) {
                ${StyledTableHeaderCell} {
                    padding: 0;
                }
            }

            ${StyledTableHeaderCell} {
                padding: 0 16px;
                height: 32px;
            }
        }
    }
    ${StyledWrapperTableBody} {
        height: 100%;
        overflow: auto;

        ${StyledTableBody} {
            ${StyledTableColumn} {
                ${StyledTableCell} {
                    padding: 20px 15px;

                    &:last-child {
                        border-bottom: unset;
                    }
                    &:hover {
                        cursor: pointer;
                    }
                    &.selected {
                        background-color: ${colorsPalettes.carbon[0]};
                    }
                }
                &:nth-child(-n + 2) {
                    ${StyledTableCell} {
                        padding: 0;
                        display: none;
                    }
                }
            }
        }
    }
`;

export const StyledExampleCell = styled.div`
    display: flex;
    color: ${colorsPalettes.carbon[250]};
    word-break: break-all;
`;

export const StyledOverviewCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

export const StyledOverviewActions = styled.div`
    display: flex;
`;

export const StyledDropdown = styled(Dropdown)`
    background-color: ${colorsPalettes.carbon[0]};
    ${setFontVariant("body2")};
`;

export const StyledDropdownWrapper = styled.div<{ isFocused?: boolean }>`
    width: 100%;

    ${({ isFocused }) =>
        isFocused &&
        css`
            & .DropdownButton {
                border: solid 1px ${colorsPalettes.navigation.ACTIVE_BLUE};
            }
        `}

    & .DropdownButton {
        background: ${colorsPalettes.carbon[0]};

        ${setFontVariant("body2", colorsPalettes.carbon[400])}
    }

    & .DropdownButton--disabled {
        background: ${colorsPalettes.bluegrey[200]};
        color: ${colorsPalettes.midnight[100]};
    }

    & .DropdownButton:hover {
        border: 1px solid ${colorsPalettes.navigation.ACTIVE_BLUE};
    }

    .DropdownButton-text {
        padding-left: 0;
        max-width: 380px;
        display: block;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;

        @media (min-width: 1920px) {
            max-width: 400px;
        }
    }
`;

export const StyledCellContainer = styled.div<{ isDisabled?: boolean }>`
    position: relative;
    width: 100%;

    & .DropdownContent-container {
        min-width: ${CRM_EXPORT_DROPDOWN_WIDTH}px;
        width: auto !important;
    }

    & .DropdownContent-search {
        ${({ isDisabled }) => {
            return (
                isDisabled &&
                css`
                    pointer-events: none;
                `
            );
        }}
    }
`;

export const StyledValue = styled.div`
    ${setFontVariant("body2")}
    text-align: left;

    text-overflow: ellipsis;
    overflow-wrap: normal;
    white-space: nowrap;
    overflow: hidden;
`;

export const StyledExample = styled.div`
    ${setFont({
        $size: 14,
        $family: $robotoFontFamily,
        $color: colorsPalettes.carbon[250],
    })};
    text-align: left;
`;

export const StyledDropdownItem = styled(EllipsisDropdownItem)`
    height: 100%;
    padding: 24px 0 17px 16px;
    line-height: 20px;
    justify-content: start;

    & > div:nth-child(2) {
        width: 90%;
    }
`;

export const StyledEmptyStateWrapper = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 500px;
    ${setFontVariant("subtitle2")}
`;

export const StyledExampleInput = styled.input<{ isNotValid?: boolean }>`
    height: 38.98px !important;
    margin: 0 !important;
    padding-right: 28px !important;
    width: 100%;

    &:focus {
        border-color: ${colorsPalettes.navigation.ACTIVE_BLUE} !important;
    }

    &::placeholder {
        ${setFontVariant("input2", colorsPalettes.carbon[200])};
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type="number"] {
        -moz-appearance: textfield;
    }

    &[disabled] {
        background: ${colorsPalettes.bluegrey[200]};
        cursor: not-allowed;
    }

    ${({ isNotValid }) =>
        isNotValid
            ? css`
                  border: 1px solid ${colorsPalettes.red["s100"]} !important;
              `
            : css``}
`;

export const PredefinedWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    position: relative;
    width: 100%;

    ${StyledExampleInput} {
        padding-left: 14px;
        ${setFontVariant("input2", colorsPalettes.carbon[400])};
        border: 1px solid ${colorsPalettes.carbon[200]};
    }
`;

export const StyledErrorInfoIcon = styled(SWReactIcons)`
    path {
        fill: ${colorsPalettes.red["s100"]};
    }
`;

export const StyledIconWrapper = styled.div`
    position: absolute;
    left: 8px;
    top: 15px;
`;

export const StyledTooltipContentWrapper = styled.div`
    position: absolute;
    margin-right: 10px;
`;

export const StyledLoader = styled(PixelPlaceholderLoader)`
    padding: 13px 5px 5px;
`;

export const StyledCrmDropdownItem = styled<
    FC<IEllipsisDropdownItemProps & { extraPadding?: boolean; children?: ReactNode }>
>(EllipsisDropdownItem)`
    padding-left: ${(props) => (props.extraPadding ? "48px" : "16px")};

    & > div:first-child {
        overflow: hidden;
    }
`;

export const StyledCrmDropdownItemGroup = styled<
    FC<IEllipsisDropdownItemProps & { preventDefault?: boolean; children?: ReactNode }>
>(EllipsisDropdownItem)`
    font-weight: 500;
    cursor: auto;

    &:hover,
    &:active {
        background-color: ${colorsPalettes.carbon[0]};
    }
`;

export const StyledTechnographicsWrapper = styled(StyledDropdownWrapper)<{
    placement: string;
    hasValue: boolean;
}>`
    width: 100%;

    ${StyledDropdownContent} {
        min-width: 480px;
        ${({ placement }) =>
            (placement === "top" || placement === "top-left") &&
            css`
                transform: translateY(-100%);
            `}
    }

    .DropdownButton-text {
        max-width: 192px;
        padding-left: 0;
        color: ${({ hasValue }) => (hasValue ? colorsPalettes.carbon[400] : colorsPalettes.carbon[200])};
    }
`;
